import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  handleActivityElementSelections,
  handleRemoveActivityElementSelections,
  handleUpdateActivitiesSelection,
} from "@/assignments/utils/assignmentActions";
import formatCustomQuizForAssignment from "@/assignments/utils/formatCustomQuizForAssignment";
import useLazyContentMetadataGet from "@/content/useLazyContentMetadataGet/useLazyContentMetadataGet";
import {
  AttachmentLevel,
  AttachmentWrapper,
} from "@/types/models/ContentAttachment.interface";
import elaReadingSkillsMapping from "@/view/TeacherActivityContainer/utils/elaReadingSkillsMapping";
import splitReadingSkillName from "@/view/TeacherActivityContainer/utils/splitReadingSkillName";
import filterAttachmentsByLanguage from "@/view/utils/filterAttachmentsByLanguage";
import groupAttachmentsBySubjectType from "@/view/utils/groupAttachmentsBySubjectType";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";

const ELA = "ELA";
const READING_SKILLS = "READING_SKILLS";
const IS_READING_SKILL_SELECTED = "isReadingSkillSelected";
const QUIZ = "QUIZ";

interface QuizQuestion {
  isQuizQuestionSelected: boolean;
}

type ReadingSkillStateValue = {
  isReadingSkillSelected: boolean;
} & {
  [key: string]: QuizQuestion | boolean;
};

// TODO: switch from [key: string] to [K key READING_SKILLS_ENUM]
interface ReadingSkillsState {
  [key: string]: ReadingSkillStateValue;
}

interface ELAQuizData {
  quizId: string;
  levels: string[];
}

export enum QUIZ_SELECTION_ACTION {
  ADD = "add",
  REMOVE = "remove",
}

// TODO: update this with specific types when those get introduced
export interface UseReadingSkills {
  allElaLanguagedQuizzes: AttachmentWrapper[] | undefined;
  currentElaLeveledQuizId: string | undefined;
  elaQuizData: object | undefined;
  getQuizQuestionMetadata: Function | undefined;
  getQuizQuestionMetadataAndSetState: Function | undefined;
  handleQuizActivitySelection: Function | undefined;
  handleQuizQuestionSelection: Function | undefined;
  handleReadingSkillSelection: Function | undefined;
  isUpdatingAssignment: boolean | undefined;
  quizQuestionMetadata: object | undefined;
  readingSkillState: ReadingSkillsState | undefined;
  setIsUpdatingAssignment: Function | undefined;
  setReadingSkillState: Function | undefined;
}

const ReadingSkillsContext = createContext<UseReadingSkills>({
  allElaLanguagedQuizzes: [],
  currentElaLeveledQuizId: "",
  elaQuizData: undefined,
  getQuizQuestionMetadata: undefined,
  getQuizQuestionMetadataAndSetState: undefined,
  handleQuizActivitySelection: undefined,
  handleQuizQuestionSelection: undefined,
  handleReadingSkillSelection: undefined,
  isUpdatingAssignment: false,
  quizQuestionMetadata: undefined,
  readingSkillState: undefined,
  setIsUpdatingAssignment: undefined,
  setReadingSkillState: undefined,
});

export const ReadingSkillsContextProvider = ({
  children,
  currentLevelLanguage,
}: {
  children: ReactNode;
  currentLevelLanguage: string | null | undefined;
}) => {
  const [fetchMetadata] = useLazyContentMetadataGet();
  const { attachments, setVisibleActivityIds, visibleActivityIds } =
    useAttachmentsContext() as {
      attachments: object[];
      setVisibleActivityIds: (a: string[]) => void;
      visibleActivityIds: string[];
    };
  const { dispatch, updatedAssignment } = useAssignmentReducerContext();

  const quizQuestionMetadata = useRef();
  const [readingSkillState, setReadingSkillState] =
    useState<ReadingSkillsState>({});
  const [currentElaLeveledQuizId, setCurrentElaLeveledQuizId] = useState();
  const [elaQuizData, setElaQuizData] = useState<ELAQuizData | undefined>(
    undefined,
  ) as [ELAQuizData, Function];
  const [allElaLanguagedQuizzes, setAllElaLanguagedQuizzes] = useState();
  const [isUpdatingAssignment, setIsUpdatingAssignment] = useState(false);

  const handleQuizActivitySelection = (action: QUIZ_SELECTION_ACTION) => {
    for (const [key, value] of Object.entries(readingSkillState)) {
      const questionIdsArray = Object.keys(value).filter(
        (data) => data !== IS_READING_SKILL_SELECTED,
      );

      if (action === "add") {
        questionIdsArray.forEach((id) => {
          setReadingSkillState((prevState: ReadingSkillsState) => ({
            ...prevState,
            [key]: {
              ...prevState[key as keyof ReadingSkillsState],
              [id]: { isQuizQuestionSelected: true },
              isReadingSkillSelected: true,
            },
          }));
        });
      } else if (action === "remove") {
        questionIdsArray.forEach((id) => {
          setReadingSkillState((prevState: ReadingSkillsState) => ({
            ...prevState,
            [key]: {
              ...prevState[key as keyof ReadingSkillsState],
              [id]: { isQuizQuestionSelected: false },
              isReadingSkillSelected: false,
            },
          }));
        });
      }
    }
  };

  const handleQuizQuestionSelection = (newState: ReadingSkillsState) => {
    for (const [skill, value] of Object.entries(newState)) {
      const questionIdsArray = Object.keys(value).filter(
        (data) => data !== IS_READING_SKILL_SELECTED,
      );
      const allQuestionsAreDeselected = questionIdsArray.every((id) => {
        const selection = value[id] as QuizQuestion;
        return !selection.isQuizQuestionSelected;
      });
      const allQuestionsAreSelected = questionIdsArray.every((id) => {
        const selection = value[id] as QuizQuestion;
        return selection.isQuizQuestionSelected;
      });
      const atleastOneQuestionIsSelected = questionIdsArray.some((id) => {
        const selection = value[id] as QuizQuestion;
        return selection.isQuizQuestionSelected;
      });

      if (allQuestionsAreDeselected) {
        setReadingSkillState((prevState: ReadingSkillsState) => ({
          ...prevState,
          [skill]: {
            ...prevState[skill],
            isReadingSkillSelected: false,
          },
        }));
      }

      if (allQuestionsAreSelected) {
        setReadingSkillState((prevState: ReadingSkillsState) => ({
          ...prevState,
          [skill]: {
            ...prevState[skill],
            isReadingSkillSelected: true,
          },
        }));
      }

      if (!value.isReadingSkillSelected && atleastOneQuestionIsSelected) {
        setReadingSkillState((prevState: ReadingSkillsState) => ({
          ...prevState,
          [skill]: {
            ...prevState[skill],
            isReadingSkillSelected: true,
          },
        }));
        return;
      }
    }
  };

  const handleReadingSkillSelection = (newState: ReadingSkillsState) => {
    for (const [key, value] of Object.entries(newState)) {
      const questionIdsArray = Object.keys(value).filter(
        (data) => data !== IS_READING_SKILL_SELECTED,
      );

      if (value.isReadingSkillSelected) {
        questionIdsArray.forEach((id) => {
          setReadingSkillState((prevState: ReadingSkillsState) => ({
            ...prevState,
            [key]: {
              ...prevState[key],
              isReadingSkillSelected:
                prevState[key]?.isReadingSkillSelected || false,
              [id]: { isQuizQuestionSelected: true },
            },
          }));
        });
      }

      if (!value.isReadingSkillSelected) {
        questionIdsArray.forEach((id) => {
          setReadingSkillState((prevState: ReadingSkillsState) => ({
            ...prevState,
            [key]: {
              ...prevState[key],
              isReadingSkillSelected:
                prevState[key]?.isReadingSkillSelected || false,
              [id]: { isQuizQuestionSelected: false },
            },
          }));
        });
      }
    }
  };

  // TODO: Move this out of this hook, switch to quizIds and define types.
  const getQuizQuestionMetadata = async (
    elaQuizQuestionIds: string[],
  ): Promise<object[]> => {
    return await Promise.all(
      elaQuizQuestionIds.map((id) => fetchMetadata({ contentId: id })),
    );
  };

  // TODO: Extract isReadingSkillSelected from the ReadingSkillStateValue into its own state to keep type consistency.
  // Fetch the ELA quiz questions metadata and set state.
  // Initial reading skill state object is formatted like this:
  // {
  //  'Main Idea': {
  //      isReadingSkillSelected: true,
  //      '<question_id>': { isQuizQuestionSelected: true },
  //      '<question_id>': { isQuizQuestionSelected: true }
  //    },
  //  'What the Text Says: {
  //      isReadingSkillSelected: true,
  //      '<question_id>': { isQuizQuestionSelected: true },
  //      '<question_id>': { isQuizQuestionSelected: true }
  //    },
  // }
  const getQuizQuestionMetadataAndSetState = async ({
    allElaQuizzes,
    activityElementSelections,
    currentElaQuizId,
    currentElaQuizQuestionIds,
    selectedAssignment,
  }: {
    allElaQuizzes: AttachmentLevel[];
    // TODO: Introduce exact types for activityElementSelections and selectedAssignment
    activityElementSelections: { [key: string]: { [key: string]: string } };
    currentElaQuizId: string;
    currentElaQuizQuestionIds: string[];
    selectedAssignment: object;
  }) => {
    const elaQuizQuestionMetadataResponse = await getQuizQuestionMetadata(
      currentElaQuizQuestionIds,
    );
    const savedCustomQuestions = activityElementSelections
      ? activityElementSelections[elaQuizData?.quizId]?.[currentElaQuizId]
      : null;

    // TODO: Define metadata types.
    // Set an object with quiz question ids as keys and its metadata as the value.
    // @ts-expect-error need to define metadata type
    quizQuestionMetadata.current = elaQuizQuestionMetadataResponse.reduce(
      (acc, value, index) => {
        // @ts-expect-error need to define metadata type
        acc[currentElaQuizQuestionIds[index]] = value;
        return acc;
      },
      {},
    );

    // Initiate state object
    const initialState = elaQuizQuestionMetadataResponse.reduce(
      (acc, value, index) => {
        // @ts-expect-error need to define types.
        const readingSkill = value?.data?.find(
          // @ts-expect-error need to define types.
          (item) => item.content_type === READING_SKILLS,
        );
        const splitReadingSkillTitle = splitReadingSkillName(
          readingSkill?.data?.name,
        );
        // @ts-expect-error need to define types.
        const readingSkillTitle = elaReadingSkillsMapping[
          splitReadingSkillTitle
        ]
          ? // @ts-expect-error need to define types.
            elaReadingSkillsMapping[splitReadingSkillTitle]
          : splitReadingSkillTitle;
        const activityMemberIds = selectedAssignment
          ? // @ts-expect-error need to define types.
            selectedAssignment?.activity_members.map((activity) => {
              return activity.content_id;
            })
          : null;
        const quizNotAssigned = !activityMemberIds?.includes(
          elaQuizData?.quizId,
        );

        // If assignment exists but the quiz is deselected.
        if (
          !!selectedAssignment &&
          !activityElementSelections &&
          quizNotAssigned
        ) {
          // @ts-expect-error need to define types.
          acc[readingSkillTitle] = {
            // @ts-expect-error need to define types.
            ...acc[readingSkillTitle],
            isReadingSkillSelected: false,
            // @ts-expect-error need to define types.
            [currentElaQuizQuestionIds[index]]: {
              isQuizQuestionSelected: false,
            },
          };
          return acc;
        }

        if (activityElementSelections) {
          const isQuestionSelected = savedCustomQuestions?.includes(
            // @ts-expect-error need to define types.
            currentElaQuizQuestionIds[index],
          );

          // @ts-expect-error need to define types.
          acc[readingSkillTitle] = {
            // @ts-expect-error need to define types.
            ...acc[readingSkillTitle],
            isReadingSkillSelected: true,
            // @ts-expect-error need to define types.
            [currentElaQuizQuestionIds[index]]: {
              isQuizQuestionSelected: isQuestionSelected,
            },
          };
        } else {
          // Default initial state
          // This also covers the case for an assigned quiz with no customization
          // @ts-expect-error need to define types.
          acc[readingSkillTitle] = {
            // @ts-expect-error need to define types.
            ...acc[readingSkillTitle],
            isReadingSkillSelected: true,
            // @ts-expect-error need to define types.
            [currentElaQuizQuestionIds[index]]: {
              isQuizQuestionSelected: true,
            },
          };
        }

        return acc;
      },
      {},
    );
    // @ts-expect-error need to define types.
    setAllElaLanguagedQuizzes(allElaQuizzes);
    // @ts-expect-error need to define types.
    setCurrentElaLeveledQuizId(currentElaQuizId);
    setReadingSkillState(initialState as ReadingSkillsState);
    // Synchronize reading skill checkboxes with question selection state
    // @ts-expect-error need to define types.
    handleQuizQuestionSelection(initialState);
  };

  // Handles assignment data when reading skill state changes.
  useEffect(() => {
    const handleReadingSkillData = async () => {
      if (readingSkillState && elaQuizData) {
        // @ts-expect-error need to define types.
        const allElaQuizzesIds = allElaLanguagedQuizzes?.map((c) => c.id);
        const allQuestionsDeselected = Object.values(readingSkillState).every(
          (obj) => {
            return !obj.isReadingSkillSelected;
          },
        );
        const allQuestionsAreSelected = Object.values(readingSkillState).every(
          (obj) => {
            const nestedValues = Object.values(obj);
            const filteredNestedValues = nestedValues.filter(
              (val): val is QuizQuestion => typeof val !== "boolean",
            );
            return filteredNestedValues.every(
              (nestedValue) => nestedValue.isQuizQuestionSelected === true,
            );
          },
        );

        if (allQuestionsDeselected) {
          const newVisibleActivityIds = visibleActivityIds?.filter(
            (activityId) => !allElaQuizzesIds?.includes(activityId),
          );

          setIsUpdatingAssignment(true);
          handleRemoveActivityElementSelections(dispatch);
          handleUpdateActivitiesSelection(
            { action: "remove", attachmentIds: allElaQuizzesIds },
            dispatch,
          );
          setVisibleActivityIds(newVisibleActivityIds);
          setIsUpdatingAssignment(false);
          return;
        }

        if (allQuestionsAreSelected) {
          setIsUpdatingAssignment(true);
          handleRemoveActivityElementSelections(dispatch);
          handleUpdateActivitiesSelection(
            { action: "add", attachmentIds: allElaQuizzesIds },
            dispatch,
          );
          setVisibleActivityIds([
            ...new Set([...visibleActivityIds, ...allElaQuizzesIds]),
          ]);
          setIsUpdatingAssignment(false);
          return;
        }

        // When custom quiz, update assignment reducer state with activity element selections.
        setIsUpdatingAssignment(true);
        handleUpdateActivitiesSelection(
          { action: "add", attachmentIds: allElaQuizzesIds },
          dispatch,
        );
        setVisibleActivityIds([
          ...new Set([...visibleActivityIds, ...allElaQuizzesIds]),
        ]);

        const formattedCustomQuiz = await formatCustomQuizForAssignment({
          // @ts-expect-error need to define types.
          allElaLanguagedQuizzes,
          currentElaLeveledQuizId,
          elaQuizData,
          getQuizQuestionMetadata,
          readingSkillState,
        });

        if (formattedCustomQuiz) {
          handleActivityElementSelections(formattedCustomQuiz, dispatch);
        }
        setIsUpdatingAssignment(false);
      }
    };
    void handleReadingSkillData();
  }, [JSON.stringify(readingSkillState), currentElaLeveledQuizId]);

  useEffect(() => {
    if (attachments && currentLevelLanguage) {
      // TODO: Transition the following fucntions to TS
      const filteredAttachmentsByLanguage = filterAttachmentsByLanguage(
        attachments,
        currentLevelLanguage,
      );
      const attachmentsGroupedBySubject = groupAttachmentsBySubjectType(
        filteredAttachmentsByLanguage,
      );
      // @ts-expect-error need to define types.
      const elaQuiz = attachmentsGroupedBySubject[ELA]?.find(
        // @ts-expect-error need to define types.
        (activity) => activity.content_type === QUIZ,
      );

      // Compare if current quiz id matches our current quiz state variable to track change.
      const hasLevelChanged = elaQuiz?.id !== elaQuizData?.quizId;

      if (elaQuiz && (hasLevelChanged || !elaQuizData)) {
        setElaQuizData({
          quizId: elaQuiz?.id,
          levels: elaQuiz?.data?.levels,
        });
      }
    }
  }, [attachments, JSON.stringify(elaQuizData), currentLevelLanguage]);

  useEffect(() => {
    // Ensures we remove element selections from the payload if quiz is not included in the assignment.
    const hasActivityElementSelections =
      !!updatedAssignment?.activity_element_selections;
    const quizIncludedInActivityMembers =
      updatedAssignment?.activity_members?.some((activity) => {
        return activity.content_id === elaQuizData?.quizId;
      });
    if (
      elaQuizData &&
      hasActivityElementSelections &&
      !quizIncludedInActivityMembers
    ) {
      handleRemoveActivityElementSelections(dispatch);
    }
  }, [JSON.stringify(updatedAssignment), JSON.stringify(elaQuizData)]);

  return (
    <ReadingSkillsContext.Provider
      value={{
        allElaLanguagedQuizzes,
        currentElaLeveledQuizId,
        elaQuizData,
        getQuizQuestionMetadata,
        getQuizQuestionMetadataAndSetState,
        handleQuizActivitySelection,
        handleQuizQuestionSelection,
        handleReadingSkillSelection,
        isUpdatingAssignment,
        quizQuestionMetadata: quizQuestionMetadata.current,
        readingSkillState,
        setIsUpdatingAssignment,
        setReadingSkillState,
      }}
    >
      {children}
    </ReadingSkillsContext.Provider>
  );
};

const useReadingSkillsContext = (): UseReadingSkills => {
  const readingSkillsContext = useContext(ReadingSkillsContext);

  if (readingSkillsContext === undefined) {
    throw new Error(
      "readingSkillsContext must be used within a ReadingSkillsContextProvider.",
    );
  }

  return readingSkillsContext;
};

export default useReadingSkillsContext;
